import { SearchOutlined } from '@ant-design/icons';
import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { getTranslation } from '@mahawi/eshop-common/src/translation';
import { type ICustomerProductDetail } from '@mahawi/eshop-common/src/types';
import { Col, Form, Input, Row } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { type Key, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Link from '../components/link';
import Table from '../components/table';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { search } from '../reducers/search/reducer';
import { SPACING } from '../theme/tokens';

interface IForm {
  query: string;
}

interface IProduct extends ICustomerProductDetail {
  key: string;
}

function SearchContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const searchProducts = useSelector((s: RootState) => s.Search.products);
  const searchInProcess = useSelector((s: RootState) => s.Search.inProcess);
  const searchUpdatedAt = useSelector((s: RootState) => s.Search.updatedAt);
  const languageType = useSelector((s: RootState) => s.Language.languageType);

  const [form] = Form.useForm<IForm>();
  const [dataSource, setDataSource] = useState<IProduct[]>([]);
  const [columns, setColumns] = useState<ColumnsType<object>>([]);

  useEffect((): void => {
    const dataSourceUE =
      searchProducts?.map((product: ICustomerProductDetail) => {
        return {
          key: product.uuid,
          ...product,
        };
      }) || [];

    const columnsUE: ColumnsType<object> = [
      {
        title: 'Brand',
        dataIndex: ['brand', 'name'],
        sorter: (a: IProduct, b: IProduct): number =>
          stringCompare(a.brand?.name, b.brand?.name),
        filterSearch: true,
        filters: [...new Set(dataSourceUE.map(({ brand }) => brand?.name))]
          .filter(notEmpty)
          .map((name) => ({
            text: name,
            value: name,
          }))
          .sort((a, b) => stringCompare(a.value, b.value)),
        onFilter: (value: boolean | Key, record: IProduct): boolean =>
          record.brand?.name === value,
        width: '10%',
      },
      {
        title: 'Name',
        dataIndex: 'names',
        render: (_text, record: IProduct): React.ReactElement => {
          const name: string | null = getTranslation(
            record.names,
            languageType,
          );

          return (
            <Link
              to={`/product/${record.uuid}`}
              label={name || EMPTY_VALUE}
              openInNewTab
            />
          );
        },
        width: '35%',
      },
      {
        title: 'MPN',
        dataIndex: 'mpn',
        sorter: (a: IProduct, b: IProduct): number =>
          stringCompare(a.mpn, b.mpn),
        filterSearch: true,
        filters: [...new Set(dataSourceUE.map(({ mpn }) => mpn))]
          .filter((mpn): mpn is string => !!mpn)
          .map((name) => ({
            text: name,
            value: name,
          }))
          .sort((a, b) => stringCompare(a.value, b.value)),
        onFilter: (value, record: IProduct): boolean => record.mpn === value,
        width: '10%',
      },
      {
        title: 'GTIN',
        dataIndex: 'gtin',
        sorter: (a: IProduct, b: IProduct): number =>
          stringCompare(a.gtin, b.gtin),
        filterSearch: true,
        filters: [...new Set(dataSourceUE.map(({ gtin }) => gtin))]
          .filter(notEmpty)
          .map((name) => ({
            text: name,
            value: name,
          }))
          .sort((a, b) => stringCompare(a.value, b.value)),
        onFilter: (value, record: IProduct) => record.gtin === value,
        width: '10%',
      },
    ];

    setDataSource(dataSourceUE);
    setColumns(columnsUE);
  }, [searchProducts, searchUpdatedAt, languageType?.code]);

  return (
    <Form
      form={form}
      onFinish={(values) => {
        if (values.query && languageType) {
          dispatch(search(values.query, languageType));
        }
      }}
    >
      <Row gutter={[SPACING.none, SPACING.xl]}>
        <Col span={8} offset={8} className="gutter-row">
          <Form.Item
            name="query"
            rules={[
              {
                required: true,
              },
              {
                type: 'string',
                min: 3,
              },
            ]}
          >
            <Input
              size="large"
              placeholder="Search query - product (uuid, gtin, name, description, etc.)"
              prefix={<SearchOutlined />}
              type="string"
              disabled={searchInProcess}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[SPACING.none, SPACING.xl]}>
        <Col span={24} className="gutter-row">
          <Table
            columns={columns}
            dataSource={dataSource}
            isLoading={searchInProcess && !searchProducts}
          />
        </Col>
      </Row>
    </Form>
  );
}

export default SearchContainer;
